import './index.css';
import '@stripe-internal/sail/sail.css';

import {createRoot} from 'react-dom/client';
import {MessageFormatElement} from 'intl-messageformat-parser';

import {AnalyticsReporter} from '@stripe-internal/analytics-js';
import {Cookies} from '@stripe-internal/stripe-cookies';
import {urlRedirect} from '@stripe-internal/safe-links';
import {App} from './App';
import {ExtractedApp} from './App/ExtractedApp';
import {parseConfigFromPage} from './App/helpers/parseConfigFromPage';
import {isProduction} from './App/helpers/env';
import {isIframed} from './App/helpers/isIframed';
import {config as analyticsConfig} from './App/helpers/analytics';
import type {AnalyticEvents} from './App/helpers/analytics';

const lang =
  document.getElementsByTagName('html')[0].getAttribute('lang') || 'en';
const langMap: {[lang: string]: string} = {
  en: 'en-US',
  ja: 'ja-JP',
  'en-US-x-MESSAGE-ID': 'en-US-x-MESSAGE-ID',
  'en-US-x-STAR': 'en-US-x-STAR',
};

function regexSanitizeLocale(locale: string) {
  const regExp = /^[a-z]{2,3}-([A-Z]{2}|[0-9]{3})$/; // Must start with 2-3 lowercase letters and dash, and must end with 2 uppercase letters or 3 digits
  if (regExp.test(locale)) {
    return locale;
  }
}

const sanitize = (locale: string) => {
  return langMap[lang] || regexSanitizeLocale(locale) || 'en-US';
};

export type LocaleLoader = (
  locale: string,
) => Promise<Record<string, string> | Record<string, MessageFormatElement[]>>;

export function initializeApp(
  loadLocale: LocaleLoader,
  container: HTMLElement,
) {
  const config = parseConfigFromPage();
  if (!config) {
    throw new Error('Unable to parse config from page.');
  }

  const {entrypoint, featureFlags} = config;
  const shouldUseExtractedApp =
    featureFlags.enableUniversalHelp &&
    featureFlags.useExtractedConversationsAssistant;

  const locale = sanitize(lang);

  const analytics = new AnalyticsReporter<AnalyticEvents>(analyticsConfig);

  const hasLoggedIn = new Cookies().get('__Secure-has_logged_in');

  analytics.action('support_conversations_iframe_loaded', {
    enableUniversalHelp: config.featureFlags.enableUniversalHelp,
    locale,
    audience: config.audience,
    source: config.entrypoint,
    ...(hasLoggedIn ? {has_logged_in_last_six_months: true} : {}),
  });

  const loadApp = () => {
    loadLocale(locale).then((messages) => {
      if (shouldUseExtractedApp) {
        createRoot(container).render(
          <ExtractedApp locale={locale} messages={messages} config={config} />,
        );
      } else {
        createRoot(container).render(
          <App locale={locale} messages={messages} />,
        );
      }
    });
  };

  /**
   * When in production, the app should only be rendered in an iframe.
   * Redirect to the appropriate support site when attempting to render
   * the non-iframed version in production.
   */
  // TODO(@anupriya): remove harcoded entrypoints instead enable them in flag definition
  if (isProduction() && !isIframed()) {
    if (
      shouldUseExtractedApp &&
      [
        'mobile_ios',
        'react_native_mobile_android',
        'react_native_mobile_ios',
        'mobile_link_android',
        'mobile_link_ios',
      ].includes(entrypoint)
    ) {
      loadApp();
    } else {
      urlRedirect(config.urls.supportSite);
    }
  } else {
    loadApp();
  }
}
